import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react'

import { Field } from '#app/components/form/field.tsx'
import { SelectField } from '#app/components/form/select-field.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { ResponsiveOverlay } from '#app/components/ui/responsive-overlay.tsx'
import { AddButton } from '#app/routes/mocra+/_form+/components/add-button.tsx'
import { AddressCard } from '#app/routes/mocra+/_form+/components/addresses/card.tsx'
import { AddressOverlay } from '#app/routes/mocra+/_form+/components/addresses/overlay.tsx'
import {
	isAddressDraftEmpty,
	type AddressDraft,
} from '#app/routes/mocra+/_form+/components/addresses/types.ts'
import { FACILITY_COPY } from '#app/routes/mocra+/_form+/components/facilities/constants.ts'
import {
	FACILITY_TYPE_OPTIONS,
	FEI_STATUS_PER_FACILITY,
	REGISTRATION_STATUS_OPTIONS,
} from '#app/routes/mocra+/_form+/components/facilities/schema.ts'
import {
	createFacilityDraft,
	sanitizeFacilityDraft,
	type FacilityDraft,
} from '#app/routes/mocra+/_form+/components/facilities/types.ts'

type FacilityOverlayMode = 'add' | 'edit'

type DraftErrors = Partial<Record<keyof FacilityDraft, string>>

type Props = {
	readonly mode: FacilityOverlayMode
	readonly open: boolean
	readonly onOpenChange: (open: boolean) => void
	readonly onSubmit: (draft: FacilityDraft) => void
	readonly initialValue?: FacilityDraft
	readonly onCancel?: () => void
	readonly onClosed?: () => void
}

export function FacilityOverlay({
	mode,
	open,
	onOpenChange,
	onSubmit,
	initialValue,
	onCancel,
	onClosed,
}: Props) {
	const [draft, setDraft] = useState<FacilityDraft>(
		initialValue ? { ...initialValue } : createFacilityDraft(),
	)
	const [errors, setErrors] = useState<DraftErrors>({})
	const [addressOverlayOpen, setAddressOverlayOpen] = useState(false)
	const hasAddress = !isAddressDraftEmpty(draft.address)

	useEffect(() => {
		if (open) {
			setDraft(initialValue ? { ...initialValue } : createFacilityDraft())
			setErrors({})
		}
	}, [open, initialValue])

	useEffect(() => {
		if (!open) {
			setAddressOverlayOpen(false)
			onClosed?.()
		}
	}, [open, onClosed])

	const descriptionCopy = FACILITY_COPY.description
	const titleCopy =
		mode === 'add' ? FACILITY_COPY.title.add : FACILITY_COPY.title.edit
	const submitCopy =
		mode === 'add' ? FACILITY_COPY.submit.add : FACILITY_COPY.submit.edit

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		event.stopPropagation()
		const sanitized = sanitizeFacilityDraft(draft)
		const validationErrors: DraftErrors = {}
		if (!sanitized.name) {
			validationErrors.name = 'Enter the facility name.'
		}
		if (!sanitized.address.street || !sanitized.address.city) {
			validationErrors.address =
				'Add the facility address before saving this facility.'
		}
		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors)
			return
		}
		onSubmit(sanitized)
	}

	const handleCancel = () => {
		onOpenChange(false)
		onCancel?.()
	}

	return (
		<ResponsiveOverlay
			open={open}
			onOpenChange={onOpenChange}
			title={titleCopy}
			description={descriptionCopy}
			footer={
				<div className="flex justify-end gap-3">
					<Button type="button" variant="outline" onClick={handleCancel}>
						Cancel
					</Button>
					<Button type="submit" form="facility-overlay-form">
						{submitCopy}
					</Button>
				</div>
			}
		>
			<form id="facility-overlay-form" onSubmit={handleSubmit}>
				<Field
					labelProps={{ children: FACILITY_COPY.fields.nameLabel }}
					inputProps={{
						value: draft.name,
						onChange: (event: ChangeEvent<HTMLInputElement>) =>
							setDraft((prev) => ({ ...prev, name: event.target.value })),
						type: 'text',
					}}
					errors={errors.name ? [errors.name] : undefined}
				/>
				<SelectField
					labelProps={{ children: FACILITY_COPY.fields.typeLabel }}
					selectProps={{
						value: draft.type,
						onChange: (event: ChangeEvent<HTMLSelectElement>) =>
							setDraft((prev) => ({
								...prev,
								type: event.target.value as FacilityDraft['type'],
							})),
					}}
					options={FACILITY_TYPE_OPTIONS}
				/>
				<SelectField
					labelProps={{
						children: FACILITY_COPY.fields.registrationStatusLabel,
					}}
					selectProps={{
						value: draft.registrationStatus,
						onChange: (event: ChangeEvent<HTMLSelectElement>) =>
							setDraft((prev) => ({
								...prev,
								registrationStatus: event.target
									.value as FacilityDraft['registrationStatus'],
							})),
					}}
					options={REGISTRATION_STATUS_OPTIONS}
				/>
				<SelectField
					labelProps={{ children: FACILITY_COPY.fields.feiStatusLabel }}
					selectProps={{
						value: draft.feiStatus,
						onChange: (event: ChangeEvent<HTMLSelectElement>) =>
							setDraft((prev) => ({
								...prev,
								feiStatus: event.target.value as FacilityDraft['feiStatus'],
								feiNumber:
									event.target.value === 'present' ? prev.feiNumber : '',
							})),
					}}
					options={FEI_STATUS_PER_FACILITY}
				/>
				{draft.feiStatus === 'present' ? (
					<Field
						labelProps={{ children: FACILITY_COPY.fields.feiNumberLabel }}
						inputProps={{
							value: draft.feiNumber,
							onChange: (event: ChangeEvent<HTMLInputElement>) =>
								setDraft((prev) => ({
									...prev,
									feiNumber: event.target.value,
								})),
							type: 'text',
						}}
					/>
				) : null}

				<section className="flex flex-col gap-y-1">
					<h3 className="text-sm font-medium">
						{FACILITY_COPY.fields.addressLabel}
					</h3>
					{hasAddress ? (
						<AddressCard
							title={FACILITY_COPY.fields.addressLabel}
							address={draft.address}
							onEdit={() => setAddressOverlayOpen(true)}
							onRemove={() =>
								setDraft((prev) => ({
									...prev,
									address: createFacilityDraft().address,
								}))
							}
						/>
					) : (
						<p className="text-muted-foreground text-sm">
							{FACILITY_COPY.fields.addressEmptyHint}
						</p>
					)}
					{hasAddress ? null : (
						<AddButton
							onClick={() => setAddressOverlayOpen(true)}
							className="mt-2 w-auto"
						>
							{FACILITY_COPY.actions.addAddress}
						</AddButton>
					)}
					<AddressOverlay
						mode={hasAddress ? 'edit' : 'add'}
						open={addressOverlayOpen}
						onOpenChange={setAddressOverlayOpen}
						onSubmit={(addressDraft: AddressDraft) =>
							setDraft((prev) => ({
								...prev,
								address: addressDraft,
							}))
						}
						initialValue={draft.address}
						onClosed={() => setAddressOverlayOpen(false)}
					/>
					{errors.address ? (
						<p className="text-destructive text-xs">{errors.address}</p>
					) : null}
				</section>
			</form>
		</ResponsiveOverlay>
	)
}
